/**
 * Core orchestrator:
 * Phase 1 — MIDI / QWERTY → lean PianoSynthEngine
 * Phase 2 — MetronomeEngine + beat-sync grading
 *
 * Audio unlocks automatically on first user gesture (no Enable button).
 * MIDI devices auto-preferred when present; laptop keyboard is the fallback.
 */

import { PianoSynthEngine } from "../audio/PianoSynthEngine.js";
import type { SynthVoiceOptions } from "../audio/PianoSynthEngine.js";
import {
  MetronomeEngine,
  type MetronomeBeatHandler,
  type MetronomeConfig,
  type TimeSignatureId,
} from "../audio/MetronomeEngine.js";
import {
  BeatSyncGrader,
  type SyncGradeResult,
  type SyncSessionStats,
} from "../audio/BeatSyncGrader.js";
import { MidiInterface } from "../midi/MidiInterface.js";
import type { MidiDeviceInfo, MidiInterfaceStatus, MidiNoteEvent } from "../midi/MidiInterface.js";
import { QwertyKeyboardMapper } from "../input/QwertyKeyboardMapper.js";
import type { QwertyNoteEvent } from "../input/QwertyKeyboardMapper.js";
import { PiecePlayer } from "../music/PiecePlayer.js";
import type { PiecePlayerEvent } from "../music/PiecePlayer.js";
import type { MusicPiece } from "../music/types.js";
import { getPieceById, listPieces } from "../music/library.js";
import { parseMidiFile } from "../midi/MidiFileParser.js";
import {
  midiFileToMusicPiece,
  type MidiImportOptions,
} from "../midi/midiToPiece.js";
import type { ParsedMidiFile } from "../midi/MidiFileParser.js";
import type { NotationMode } from "../notation/notation.js";
import {
  ClassSessionRunner,
  buildClassPlan,
  clampClassMinutes,
} from "../curriculum/ClassSessionRunner.js";
import { listStages, getClassById, listAllClasses } from "../curriculum/curriculum.js";
import type {
  ClassPlan,
  ClassSessionStatus,
  TutorClass,
  TutorStage,
} from "../curriculum/types.js";
import { FollowAlongEngine } from "../practice/FollowAlongEngine.js";
import type { FollowAlongStats } from "../practice/FollowAlongEngine.js";
import { TimedPlayheadGrader } from "../practice/TimedPlayheadGrader.js";
import { PedalCueGrader } from "../practice/PedalCueGrader.js";
import {
  PerformanceRecorder,
  type RecordedPerformance,
} from "../performance/PerformanceRecorder.js";
import {
  ExamSession,
  getExamForStage,
  STAGE_EXAMS,
  applyExamPass,
  applyClassComplete,
  stageClassesCompleted,
  DEFAULT_PROGRESS,
  MAX_CURRICULUM_STAGE,
  type ProgressState,
  type ExamStatus,
} from "../exam/exams.js";

export type InputSource = "midi" | "qwerty" | "api";

/** Which physical input the student is using to play. */
export type PlayInputMode = "midi" | "laptop";

export interface TutorNoteEvent {
  type: "noteon" | "noteoff";
  midiNote: number;
  velocity: number;
  source: InputSource;
  timestamp: number;
  sync?: SyncGradeResult;
}

export interface ExamAttemptData {
  examId: string;
  notes: Array<{ midi: number; atMs: number }>;
  clientDurationMs: number;
}

export type TutorNoteHandler = (event: TutorNoteEvent) => void;
export type TutorStatusHandler = (status: CoreEngineStatus) => void;

export interface MetronomeStatus {
  running: boolean;
  bpm: number;
  timeSignature: TimeSignatureId;
  volume: number;
  lockGrading: boolean;
  syncStats: SyncSessionStats;
}

export type ImportedPracticeMode = "learn" | "performance";

export interface ImportedPracticeStatus {
  active: boolean;
  mode: ImportedPracticeMode | null;
  title: string | null;
  expectedMidis: number[];
  expectedFingers: Record<number, 1 | 2 | 3 | 4 | 5>;
  stats: FollowAlongStats | null;
  completed: boolean;
}

export interface CoreEngineStatus {
  audioReady: boolean;
  audioMuted: boolean;
  midi: MidiInterfaceStatus;
  midiDevices: MidiDeviceInfo[];
  /** MIDI hardware listening (auto on when a device appears). */
  midiListening: boolean;
  /** Active play source preference. */
  playInput: PlayInputMode;
  qwertyEnabled: boolean;
  masterVolume: number;
  activeNotes: number[];
  metronome: MetronomeStatus;
  /** Web Audio baseLatency (render quantum). */
  baseLatencyMs: number;
  /** Browser-reported outputLatency (OS mixer buffer), when available. */
  outputBufferMs: number;
  /** base + output buffer (used for grading compensation). */
  outputLatencyMs: number;
  /** worklet = native audio thread; main-thread = oscillator fallback. */
  audioBackend: "native" | "worklet" | "main-thread" | "none";
  notationMode: NotationMode;
  classSession: ClassSessionStatus;
  exam: ExamStatus;
  progress: ProgressState;
  /** True while stage exam is running — UI must hide helpers. */
  blindMode: boolean;
  /** Short-lived hit/wrong flashes for piano key coloring. */
  keyFeedback: KeyFeedbackFlash[];
  importedPractice: ImportedPracticeStatus;
}

export interface KeyFeedbackFlash {
  midi: number;
  kind: "hit" | "wrong";
  /** performance.now() expiry */
  until: number;
}

export class PianoTutorCore {
  readonly synth = new PianoSynthEngine();
  readonly midi = new MidiInterface();
  readonly qwerty = new QwertyKeyboardMapper({ baseOctave: 4, shiftOctaveUp: true });
  readonly metronome = new MetronomeEngine();
  readonly syncGrader = new BeatSyncGrader();

  private noteHandlers = new Set<TutorNoteHandler>();
  private statusHandlers = new Set<TutorStatusHandler>();
  private beatHandlers = new Set<MetronomeBeatHandler>();
  private activeNotes = new Set<number>();
  private masterVolume = 0.75;
  private unsubscribers: Array<() => void> = [];
  private started = false;
  private statusRaf: number | null = null;
  private midiDevicesCache: MidiDeviceInfo[] = [];
  private midiListening = true;
  private playInput: PlayInputMode = "laptop";
  /** When false, hotplug will not override a manual laptop/midi choice. */
  private autoPreferMidi = true;
  private gestureUnlockBound = false;
  private notationMode: NotationMode = "letter";
  readonly piecePlayer: PiecePlayer;
  private importedPiece: MusicPiece | null = null;
  private activePracticePiece: MusicPiece | null = null;
  private lastParsedMidi: ParsedMidiFile | null = null;
  private classRunner: ClassSessionRunner;
  private classPlan: ClassPlan | null = null;
  private followAlong = new FollowAlongEngine();
  private pedalCueGrader = new PedalCueGrader();
  private practiceWait: {
    resolve: (stats: FollowAlongStats) => void;
  } | null = null;
  private examSession = new ExamSession();
  private examAttemptId: string | null = null;
  private examAttemptStartedAt = 0;
  private examAttemptNotes: Array<{ midi: number; atMs: number }> = [];
  private progress: ProgressState = { ...DEFAULT_PROGRESS };
  private keyFeedback = new Map<number, KeyFeedbackFlash>();
  private keyFeedbackClearTimer: ReturnType<typeof setTimeout> | null = null;
  private practiceTimelineOrigin: number | null = null;
  private classMetronomeLocked = false;
  private importedMode: ImportedPracticeMode | null = null;
  private performanceGrader = new TimedPlayheadGrader();
  private performanceRecorder = new PerformanceRecorder();
  private importedCompleted = false;
  private importedMetronomeOwned = false;
  /** Visual key timers while PiecePlayer demos a piece (so Practice piano animates). */
  private demoNoteTimers = new Map<number, ReturnType<typeof setTimeout>>();
  /** Sync result produced by gradePracticeOrExam; consumed once by the UI event path. */
  private pendingSyncGrade: SyncGradeResult | undefined;

  constructor() {
    this.piecePlayer = new PiecePlayer(this.synth);
    this.piecePlayer.onEvent((e) => this.handlePiecePlayerVisual(e));
    this.classRunner = new ClassSessionRunner({
      onStatus: () => this.emitStatusSoon(),
      runUserPracticeLoop: async (opts) => {
        const piece =
          opts.piece ??
          (opts.pieceId ? getPieceById(opts.pieceId) : undefined);
        if (!piece) {
          throw new Error(`Missing library piece: ${opts.pieceId ?? "(none)"}`);
        }
        await this.ensureAudio();
        if (opts.assessment === "ear") {
          // Ear classes intentionally demonstrate the phrase once, then the
          // normal wait-for-correct-key grader takes over from memory.
          await this.piecePlayer.playAsync(piece, opts.bpm);
        }
        this.classMetronomeLocked = !!opts.metronomeLocked;
        this.practiceTimelineOrigin = null;
        this.activePracticePiece = piece;
        this.followAlong.load(piece);
        this.pedalCueGrader.load(piece.pedalCues);
        this.classRunner.setExpectedMidis(this.followAlong.getExpectedMidiList());
        this.classRunner.setExpectedFingers(this.followAlong.getExpectedFingerMap());
        this.classRunner.setGradingHint(null);
        this.emitStatusNow();

        return await new Promise<FollowAlongStats>((resolve) => {
          this.practiceWait = {
            resolve: (stats) => {
              const gradedStats = this.withPedalStats(stats);
              this.practiceWait = null;
              this.followAlong.clear();
              this.pedalCueGrader.clear();
              this.classMetronomeLocked = false;
              this.practiceTimelineOrigin = null;
              this.classRunner.setExpectedMidis([]);
              this.classRunner.setExpectedFingers({});
              this.classRunner.setGradingHint(null);
              resolve(gradedStats);
            },
          };
        });
      },
      startMetronome: async (bpm) => {
        await this.ensureAudio();
        this.metronome.setBpm(bpm);
        if (!this.metronome.isRunning) {
          if (this.synth.audioContext) this.metronome.attach(this.synth.audioContext);
          this.metronome.start();
        }
      },
      stopMetronome: () => {
        this.metronome.stop();
      },
      setMetronomeLock: (on) => {
        this.syncGrader.setEnabled(on);
        if (on) this.syncGrader.reset();
      },
    });
  }

  onNote(handler: TutorNoteHandler): () => void {
    this.noteHandlers.add(handler);
    return () => this.noteHandlers.delete(handler);
  }

  onStatus(handler: TutorStatusHandler): () => void {
    this.statusHandlers.add(handler);
    handler(this.getStatus());
    return () => this.statusHandlers.delete(handler);
  }

  onMetronomeBeat(handler: MetronomeBeatHandler): () => void {
    this.beatHandlers.add(handler);
    return () => this.beatHandlers.delete(handler);
  }

  getStatus(): CoreEngineStatus {
    return {
      audioReady: this.synth.isReady,
      audioMuted: this.synth.isMuted,
      midi: this.midi.getStatus(),
      midiDevices: this.midiDevicesCache,
      midiListening: this.midiListening,
      playInput: this.playInput,
      qwertyEnabled: this.isQwertyActive(),
      masterVolume: this.masterVolume,
      activeNotes: [...this.activeNotes].sort((a, b) => a - b),
      metronome: {
        running: this.metronome.isRunning,
        bpm: this.metronome.getBpm(),
        timeSignature: this.metronome.getTimeSignature(),
        volume: this.metronome.getVolume(),
        lockGrading: this.syncGrader.isEnabled(),
        syncStats: this.syncGrader.getStats(),
      },
      baseLatencyMs: this.synth.baseLatencySec * 1000,
      outputBufferMs: this.synth.outputBufferSec * 1000,
      outputLatencyMs: this.synth.outputLatencySec * 1000,
      audioBackend: this.synth.audioBackend,
      notationMode: this.notationMode,
      classSession: this.classRunner.getStatus(),
      exam: this.examSession.getStatus(),
      progress: { ...this.progress },
      blindMode: this.examSession.getStatus().blind,
      keyFeedback: this.collectKeyFeedback(),
      importedPractice: this.getImportedPracticeStatus(),
    };
  }

  private getImportedPracticeStatus(): ImportedPracticeStatus {
    const active = this.importedMode != null;
    const stats =
      this.importedMode === "performance"
        ? this.performanceGrader.getStats()
        : this.importedMode === "learn"
          ? this.followAlong.getStats()
          : null;
    const expectedMidis =
      this.importedMode === "performance"
        ? this.performanceGrader.getExpectedMidiList()
        : this.importedMode === "learn"
          ? this.followAlong.getExpectedMidiList()
          : [];
    const expectedFingers =
      this.importedMode === "learn"
        ? this.followAlong.getExpectedFingerMap()
        : {};
    return {
      active,
      mode: this.importedMode,
      title: this.activePracticePiece?.title ?? this.importedPiece?.title ?? null,
      expectedMidis,
      expectedFingers,
      stats,
      completed: this.importedCompleted || !!stats?.completed,
    };
  }

  private collectKeyFeedback(): KeyFeedbackFlash[] {
    const now = performance.now();
    const out: KeyFeedbackFlash[] = [];
    for (const [midi, flash] of this.keyFeedback) {
      if (flash.until <= now) this.keyFeedback.delete(midi);
      else out.push(flash);
    }
    return out;
  }

  private flashKeyFeedback(midi: number, kind: "hit" | "wrong"): void {
    const until = performance.now() + (kind === "wrong" ? 450 : 280);
    this.keyFeedback.set(midi, { midi, kind, until });
    if (this.keyFeedbackClearTimer != null) clearTimeout(this.keyFeedbackClearTimer);
    this.keyFeedbackClearTimer = setTimeout(() => {
      this.keyFeedbackClearTimer = null;
      this.emitStatusNow();
    }, kind === "wrong" ? 460 : 290);
  }

  async start(): Promise<CoreEngineStatus> {
    if (this.started) return this.getStatus();

    this.unsubscribers.push(
      this.midi.onNote((e) => this.handleMidi(e)),
      this.midi.onControlChange((e) => {
        if (e.controller === 64) {
          const down = e.value >= 64;
          this.synth.setSustainPedal(down);
          this.performanceRecorder.pedal(down);
          const graded = this.pedalCueGrader.handle(down);
          if (graded != null && this.followAlong.isActive) {
            this.classRunner.setPracticeStats(
              this.withPedalStats(this.followAlong.getStats()),
            );
            this.emitStatusSoon();
          }
        }
      }),
      this.midi.onDevicesChanged((devices) => this.onMidiDevicesChanged(devices)),
      this.qwerty.onNote((e) => this.handleQwerty(e)),
      this.metronome.onBeat((e) => {
        for (const h of this.beatHandlers) h(e);
      }),
    );

    this.qwerty.attach(window);
    this.qwerty.setEnabled(true);
    this.qwerty.setAudioSink((type, midiNote, velocity) => {
      if (!this.isQwertyActive()) return;
      if (type === "noteon") {
        this.synth.noteOn(midiNote, velocity);
        this.activeNotes.add(midiNote);
        this.pendingSyncGrade = this.gradePracticeOrExam(midiNote, velocity);
      } else {
        this.synth.noteOff(midiNote, velocity);
        this.activeNotes.delete(midiNote);
      }
    });
    this.installAutoUnlockGestures();

    await this.midi.connect();
    this.midiDevicesCache = this.midi.listInputs();
    this.applyMidiPreference(this.midiDevicesCache, /*fromHotplug*/ true);

    this.started = true;
    this.emitStatusNow();
    return this.getStatus();
  }

  /**
   * Browsers still need one user gesture to start AudioContext.
   * We hitch that to the first key/pointer/touch — no Enable button.
   */
  private installAutoUnlockGestures(): void {
    if (this.gestureUnlockBound) return;
    this.gestureUnlockBound = true;

    const unlock = () => {
      this.synth.unlockSync();
      const ctx = this.synth.audioContext;
      if (ctx) this.metronome.attach(ctx);
    };

    window.addEventListener("pointerdown", unlock, { capture: true, passive: true });
    window.addEventListener("keydown", unlock, { capture: true, passive: true });
    window.addEventListener("touchstart", unlock, { capture: true, passive: true });
  }

  async ensureAudio(): Promise<void> {
    if (this.synth.isReady && this.synth.audioContext?.state === "running") return;
    await this.synth.unlock();
    const ctx = this.synth.audioContext;
    if (ctx) this.metronome.attach(ctx);
    this.emitStatusNow();
  }

  /** Mute / unmute — the only user-facing audio power control. */
  async setAudioMuted(muted: boolean): Promise<void> {
    if (!muted && !this.synth.isUnlocked) {
      await this.ensureAudio();
    }
    this.synth.setMuted(muted);
    if (muted && this.metronome.isRunning) this.metronome.stop();
    this.emitStatusNow();
  }

  setMasterVolume(volume01: number): void {
    this.masterVolume = Math.max(0, Math.min(1, volume01));
    this.synth.setMasterVolume(this.masterVolume);
    this.emitStatusSoon();
  }

  setAdsr(options: SynthVoiceOptions): void {
    this.synth.setAdsr(options);
  }

  setNotationMode(mode: NotationMode): void {
    this.notationMode = mode;
    this.emitStatusNow();
  }

  getNotationMode(): NotationMode {
    return this.notationMode;
  }

  /**
   * Manual choice: play from MIDI keyboard or laptop keys.
   * Disables auto-prefer until MIDI hotplug with empty→nonempty if desired.
   */
  setPlayInput(mode: PlayInputMode, opts: { userChoice?: boolean } = {}): void {
    if (opts.userChoice !== false) this.autoPreferMidi = false;
    this.playInput = mode;

    if (mode === "midi") {
      this.midiListening = true;
      this.qwerty.setEnabled(false);
      void this.refreshMidi();
    } else {
      this.qwerty.setEnabled(true);
    }

    this.panic();
    this.emitStatusNow();
  }

  /** Stop accepting MIDI hardware notes (keeps devices listed). */
  disconnectMidi(): void {
    this.midiListening = false;
    this.autoPreferMidi = false;
    this.playInput = "laptop";
    this.qwerty.setEnabled(true);
    this.panic();
    this.emitStatusNow();
  }

  /** Re-enable MIDI listening and prefer it when devices exist. */
  async connectMidi(): Promise<MidiInterfaceStatus> {
    this.midiListening = true;
    this.autoPreferMidi = true;
    const status = await this.midi.connect();
    this.midiDevicesCache = this.midi.listInputs();
    this.applyMidiPreference(this.midiDevicesCache, true);
    this.emitStatusNow();
    return status;
  }

  async refreshMidi(): Promise<MidiInterfaceStatus> {
    const status = await this.midi.connect();
    this.midiDevicesCache = this.midi.listInputs();
    if (this.autoPreferMidi) {
      this.applyMidiPreference(this.midiDevicesCache, true);
    }
    this.emitStatusNow();
    return status;
  }

  configureMetronome(config: MetronomeConfig): void {
    this.metronome.configure(config);
    this.emitStatusSoon();
  }

  setMetronomeBpm(bpm: number): void {
    this.metronome.setBpm(bpm);
    this.emitStatusSoon();
  }

  setMetronomeTimeSignature(id: TimeSignatureId): void {
    this.metronome.setTimeSignature(id);
    this.emitStatusSoon();
  }

  setMetronomeVolume(volume01: number): void {
    this.metronome.setVolume(volume01);
    this.emitStatusSoon();
  }

  setMetronomeLockGrading(enabled: boolean): void {
    this.syncGrader.setEnabled(enabled);
    if (enabled) this.syncGrader.reset();
    this.emitStatusSoon();
  }

  async startMetronome(): Promise<void> {
    await this.ensureAudio();
    if (!this.synth.audioContext) {
      throw new Error("Audio unavailable");
    }
    this.metronome.attach(this.synth.audioContext);
    this.metronome.start();
    this.emitStatusNow();
  }

  stopMetronome(): void {
    this.metronome.stop();
    this.emitStatusNow();
  }

  resetMetronomeTransport(): void {
    this.metronome.resetTransport();
    this.syncGrader.reset();
    this.emitStatusSoon();
  }

  getSyncStats(): SyncSessionStats {
    return this.syncGrader.getStats();
  }

  /* —— Phase 3: Music library —— */

  listLibraryPieces(): MusicPiece[] {
    return listPieces();
  }

  getLibraryPiece(id: string): MusicPiece | undefined {
    return getPieceById(id);
  }

  async playLibraryPiece(id: string, bpmOverride?: number): Promise<void> {
    const piece = getPieceById(id);
    if (!piece) throw new Error(`Unknown piece: ${id}`);
    await this.ensureAudio();
    await this.piecePlayer.playAsync(piece, bpmOverride);
    this.emitStatusSoon();
  }

  stopLibraryPiece(): void {
    this.piecePlayer.stop();
    this.clearDemoVisualNotes();
    this.emitStatusSoon();
  }

  /** Light Practice-piano keys in sync with demo / library playback. */
  private handlePiecePlayerVisual(e: PiecePlayerEvent): void {
    if (e.type === "start") {
      this.clearDemoVisualNotes();
      this.emitStatusSoon();
      return;
    }
    if (e.type === "end") {
      this.clearDemoVisualNotes();
      this.emitStatusSoon();
      return;
    }
    if (e.type !== "note") return;

    const midi = e.note.midi;
    const ctx = this.synth.audioContext;
    const delayMs = ctx ? Math.max(0, (e.audioTime - ctx.currentTime) * 1000) : 0;
    const bpm = this.piecePlayer.currentPiece?.bpm ?? 90;
    const durMs = Math.max(90, e.note.duration * (60 / bpm) * 1000);

    const onTimer = setTimeout(() => {
      this.activeNotes.add(midi);
      this.emitStatusSoon();
      const prev = this.demoNoteTimers.get(midi);
      if (prev) clearTimeout(prev);
      const offTimer = setTimeout(() => {
        this.activeNotes.delete(midi);
        this.demoNoteTimers.delete(midi);
        this.emitStatusSoon();
      }, durMs);
      this.demoNoteTimers.set(midi, offTimer);
    }, delayMs);
    // Keep a handle so stop() can cancel pending "on" flashes too
    const cancelKey = midi + 10000;
    const prevOn = this.demoNoteTimers.get(cancelKey);
    if (prevOn) clearTimeout(prevOn);
    this.demoNoteTimers.set(cancelKey, onTimer);
  }

  private clearDemoVisualNotes(): void {
    for (const t of this.demoNoteTimers.values()) clearTimeout(t);
    this.demoNoteTimers.clear();
    // Only clear notes that were demo-driven when nothing else is holding them —
    // safest: clear all when demo ends (user isn't playing during Demo).
    this.activeNotes.clear();
  }

  /* —— Phase 6: Curriculum —— */

  listStages(): TutorStage[] {
    return listStages();
  }

  listCurriculumClasses(): TutorClass[] {
    return listAllClasses();
  }

  getCurriculumClass(classId: string): TutorClass | undefined {
    return getClassById(classId);
  }

  prepareClassSession(classId: string, minutes?: number): ClassPlan {
    this.classPlan = this.classRunner.prepare(classId, minutes);
    this.emitStatusNow();
    return this.classPlan;
  }

  previewClassPlan(classId: string, minutes: number): ClassPlan {
    const tutorClass = getClassById(classId);
    if (!tutorClass) throw new Error(`Unknown class: ${classId}`);
    return buildClassPlan(tutorClass, clampClassMinutes(tutorClass, minutes));
  }

  async startClassSession(classId?: string, minutes?: number): Promise<void> {
    this.stopClassSession();
    this.stopImportedPractice();
    this.stopLibraryPiece();
    this.stopExam();
    if (classId) this.prepareClassSession(classId, minutes);
    const prepared = this.classRunner.getStatus();
    this.performanceRecorder.start(prepared.classTitle ?? "Class performance");
    await this.classRunner.start();
    this.performanceRecorder.stop();
    if (this.classRunner.getStatus().phase === "completed" && this.classPlan) {
      this.progress = applyClassComplete(this.progress, this.classPlan.classId);
    }
    this.emitStatusNow();
  }

  stopClassSession(): void {
    if (this.practiceWait) {
      this.practiceWait.resolve(this.followAlong.getStats());
      this.practiceWait = null;
    }
    this.followAlong.clear();
    this.classRunner.stop();
    this.performanceRecorder.stop();
    this.stopLibraryPiece();
    this.emitStatusNow();
  }

  getLastRecordedPerformance(): RecordedPerformance | null {
    return this.performanceRecorder.getLast();
  }

  setProgressState(progress: ProgressState): void {
    this.progress = { ...progress };
    this.emitStatusNow();
  }

  getProgress(): ProgressState {
    return { ...this.progress };
  }

  listExams() {
    return STAGE_EXAMS;
  }

  /**
   * Gatekeeper exams are open for any stage you have unlocked.
   * Completing classes is optional practice — passing the exam unlocks the next stage.
   * To reach Stage N classes/exam you must pass exams for stages 1..N-1 (via unlockedStage).
   */
  canTakeExam(stage: number): boolean {
    if (stage < 1 || stage > MAX_CURRICULUM_STAGE) return false;
    return stage <= this.progress.unlockedStage;
  }

  startExamForStage(stage: number): void {
    const exam = getExamForStage(stage);
    if (!exam) throw new Error(`No exam for stage ${stage}`);
    if (!this.canTakeExam(stage)) {
      throw new Error(
        `Stage ${stage} exam is locked — pass Stage ${stage - 1} Gatekeeper first (exams unlock the path; classes are optional practice).`,
      );
    }
    this.stopClassSession();
    this.stopLibraryPiece();
    this.examAttemptId = exam.id;
    this.examAttemptStartedAt = performance.now();
    this.examAttemptNotes = [];
    this.examSession.start(exam.id);
    this.emitStatusNow();
  }

  stopExam(): void {
    this.examSession.stop();
    this.emitStatusNow();
  }

  getExamAttemptData(): ExamAttemptData | null {
    if (!this.examAttemptId || this.examAttemptStartedAt <= 0) return null;
    return {
      examId: this.examAttemptId,
      notes: this.examAttemptNotes.map((note) => ({ ...note })),
      clientDurationMs: Math.max(0, performance.now() - this.examAttemptStartedAt),
    };
  }

  /** Drive exam countdown; call ~1Hz while an exam is live. */
  tickExam(): void {
    if (this.examSession.getStatus().phase !== "running") return;
    const changed = this.examSession.tick();
    if (changed) {
      const st = this.examSession.getStatus();
      if (st.phase === "passed" && st.examId) {
        this.progress = applyExamPass(this.progress, st.examId);
      }
      this.emitStatusNow();
    }
  }

  private withPedalStats(stats: FollowAlongStats): FollowAlongStats {
    const pedal = this.pedalCueGrader.getStats();
    if (pedal.expected === 0) return stats;
    return {
      ...stats,
      pedalHits: pedal.hits,
      pedalMisses: pedal.misses,
    };
  }

  private gradePracticeOrExam(
    midiNote: number,
    velocity = 100,
  ): SyncGradeResult | undefined {
    const audioTime = this.synth.currentTime;
    const latencyCompSec = this.synth.outputLatencySec * 0.5;
    let sync: SyncGradeResult | undefined;

    if (this.examSession.getStatus().phase === "running") {
      if (this.examAttemptStartedAt > 0) {
        this.examAttemptNotes.push({
          midi: midiNote,
          atMs: Math.max(0, performance.now() - this.examAttemptStartedAt),
        });
      }
      const hit = this.examSession.handleNoteOn(midiNote, {
        velocity,
        audioTime,
        latencyCompSec,
      });
      if (hit) {
        const kind =
          hit.quality === "wrong"
            ? "wrong"
            : hit.quality === "perfect"
              ? "hit"
              : "hit";
        this.flashKeyFeedback(midiNote, kind);
      }
      const st = this.examSession.getStatus();
      if (st.phase === "passed" && st.examId) {
        this.progress = applyExamPass(this.progress, st.examId);
      }
      this.emitStatusSoon();
      return undefined;
    }

    if (this.importedMode === "performance" && this.performanceGrader.isActive) {
      const hit = this.performanceGrader.handleNoteOn(midiNote, audioTime);
      this.flashKeyFeedback(midiNote, hit.quality === "wrong" ? "wrong" : "hit");
      if (this.syncGrader.isEnabled() && this.metronome.isRunning) {
        sync = this.syncGrader.grade(this.metronome, audioTime - latencyCompSec);
      }
      if (this.performanceGrader.getStats().completed) {
        this.finishImportedPractice();
      } else {
        this.emitStatusSoon();
      }
      return sync;
    }

    if (this.followAlong.isActive && (this.practiceWait || this.importedMode === "learn")) {
      if (this.practiceTimelineOrigin == null) {
        this.practiceTimelineOrigin = audioTime;
      }
      const piece = this.followAlong.pieceIdLoaded
        ? getPieceById(this.followAlong.pieceIdLoaded) ?? this.activePracticePiece
        : this.activePracticePiece;
      const lock =
        this.importedMode === "learn"
          ? this.syncGrader.isEnabled() || this.metronome.isRunning
          : this.syncGrader.isEnabled() || this.classMetronomeLocked;
      const hit = this.followAlong.handleNoteOn(midiNote, {
        velocity,
        audioTime,
        bpm: piece?.bpm,
        timelineOrigin: this.practiceTimelineOrigin,
        gradeTiming: !!lock,
        gradeVelocity: true,
        latencyCompSec,
        heldMidis: [...this.activeNotes],
        strictExclusive: true,
      });
      this.flashKeyFeedback(midiNote, hit.quality === "wrong" ? "wrong" : "hit");
      if (lock && this.metronome.isRunning) {
        sync = this.syncGrader.grade(this.metronome, audioTime - latencyCompSec);
      }
      if (this.practiceWait) {
        this.classRunner.setExpectedMidis(this.followAlong.getExpectedMidiList());
        this.classRunner.setExpectedFingers(this.followAlong.getExpectedFingerMap());
        this.classRunner.setGradingHint(this.followAlong.getLastHint());
        const stats = this.withPedalStats(this.followAlong.getStats());
        this.classRunner.setPracticeStats(stats);
        if (stats.completed) {
          this.practiceWait.resolve(stats);
        } else {
          this.emitStatusSoon();
        }
      } else if (this.importedMode === "learn") {
        if (this.followAlong.getStats().completed) {
          this.finishImportedPractice();
        } else {
          this.emitStatusSoon();
        }
      }
      return sync;
    }

    return undefined;
  }

  getClassSessionStatus(): ClassSessionStatus {
    return this.classRunner.getStatus();
  }

  getImportedPiece(): MusicPiece | null {
    return this.importedPiece;
  }

  getLastParsedMidi(): ParsedMidiFile | null {
    return this.lastParsedMidi;
  }

  /**
   * Phase 4 — parse a .mid / .midi ArrayBuffer into a playable MusicPiece lesson.
   */
  importMidiFile(
    data: ArrayBuffer | Uint8Array,
    options: MidiImportOptions = {},
  ): MusicPiece {
    this.stopImportedPractice();
    const parsed = parseMidiFile(data);
    this.lastParsedMidi = parsed;
    const piece = midiFileToMusicPiece(parsed, options);
    this.importedPiece = piece;
    this.emitStatusNow();
    return piece;
  }

  async playImportedPiece(bpmOverride?: number): Promise<void> {
    if (!this.importedPiece) throw new Error("No MIDI imported yet");
    this.stopImportedPractice();
    await this.ensureAudio();
    this.piecePlayer.play(this.importedPiece, bpmOverride);
    this.emitStatusSoon();
  }

  /** Interactive Learn (wait-for-key) or Performance (continuous) for imported MIDI. */
  async startImportedPractice(mode: ImportedPracticeMode): Promise<void> {
    if (!this.importedPiece) throw new Error("No MIDI imported yet");
    await this.startPiecePractice(this.importedPiece, mode);
  }

  /** Interactive Learn/Performance for a built-in library or practice-pack piece. */
  async startLibraryPractice(id: string, mode: ImportedPracticeMode): Promise<void> {
    const piece = getPieceById(id);
    if (!piece) throw new Error(`Unknown piece: ${id}`);
    await this.startPiecePractice(piece, mode);
  }

  private async startPiecePractice(
    piece: MusicPiece,
    mode: ImportedPracticeMode,
  ): Promise<void> {
    if (this.classRunner.getStatus().phase === "running") {
      throw new Error("Finish or abort the class first");
    }
    if (this.examSession.getStatus().phase === "running") {
      throw new Error("Finish or abort the exam first");
    }
    this.stopImportedPractice();
    this.stopLibraryPiece();
    await this.ensureAudio();
    this.importedCompleted = false;
    this.importedMode = mode;
    this.activePracticePiece = piece;
    this.practiceTimelineOrigin = null;

    if (mode === "learn") {
      this.followAlong.load(piece);
      this.emitStatusNow();
      return;
    }

    // Performance: fixed timeline + optional metronome lock for beat sync feedback.
    const bpm = piece.bpm || 90;
    this.metronome.setBpm(bpm);
    if (this.synth.audioContext) this.metronome.attach(this.synth.audioContext);
    if (!this.metronome.isRunning) {
      this.metronome.start();
      this.importedMetronomeOwned = true;
    }
    this.syncGrader.setEnabled(true);
    this.syncGrader.reset();
    const origin = this.synth.currentTime + 0.05;
    this.performanceGrader.load(piece, origin);
    this.emitStatusNow();
  }

  stopImportedPractice(): void {
    if (this.importedMode == null && !this.performanceGrader.isActive && !this.importedCompleted) {
      // Still clear leftover state quietly.
    }
    this.importedMode = null;
    this.activePracticePiece = null;
    this.importedCompleted = false;
    this.performanceGrader.clear();
    if (!this.practiceWait) this.followAlong.clear();
    this.practiceTimelineOrigin = null;
    if (this.importedMetronomeOwned) {
      this.metronome.stop();
      this.importedMetronomeOwned = false;
      this.syncGrader.setEnabled(false);
    }
    this.emitStatusSoon();
  }

  private finishImportedPractice(): void {
    this.importedCompleted = true;
    this.emitStatusNow();
  }

  /** Call from UI raf while imported performance is active. */
  tickImportedPractice(): void {
    if (this.importedMode !== "performance" || !this.performanceGrader.isActive) return;
    if (this.importedCompleted) return;
    const before = this.performanceGrader.getStats().remaining;
    const done = this.performanceGrader.tick(this.synth.currentTime);
    if (done) {
      this.finishImportedPractice();
      return;
    }
    const after = this.performanceGrader.getStats().remaining;
    if (after !== before) this.emitStatusSoon();
  }

  clearImportedPiece(): void {
    this.stopImportedPractice();
    this.stopLibraryPiece();
    this.importedPiece = null;
    this.lastParsedMidi = null;
    this.emitStatusNow();
  }

  playNote(midiNote: number, velocity = 100): void {
    void this.ensureAudio().then(() => {
      this.routeNote("noteon", midiNote, velocity, "api");
    });
  }

  releaseNote(midiNote: number): void {
    this.routeNote("noteoff", midiNote, 0, "api");
  }

  /** Synchronous API play when audio already unlocked (pointer piano). */
  playNoteImmediate(midiNote: number, velocity = 100): void {
    if (!this.synth.isUnlocked) {
      // Keep the first mobile tap in the user-gesture call stack. Waiting for
      // ensureAudio() lets a quick note-off arrive before note-on, producing a
      // delayed or apparently missed key in Android WebView.
      this.synth.unlockSync();
      void this.ensureAudio();
    }
    this.routeNote("noteon", midiNote, velocity, "api");
  }

  panic(): void {
    this.synth.allNotesOff();
    this.activeNotes.clear();
    this.emitStatusSoon();
  }

  stop(): void {
    this.stopClassSession();
    this.stopLibraryPiece();
    this.stopMetronome();
    this.panic();
    this.qwerty.setAudioSink(null);
    this.qwerty.detach(window);
    this.midi.disconnect();
    for (const u of this.unsubscribers) u();
    this.unsubscribers = [];
    if (this.statusRaf != null) {
      cancelAnimationFrame(this.statusRaf);
      this.statusRaf = null;
    }
    this.synth.dispose();
    this.started = false;
    this.emitStatusNow();
  }

  private isQwertyActive(): boolean {
    return this.playInput === "laptop" || this.midiDevicesCache.length === 0 || !this.midiListening;
  }

  private isMidiActive(): boolean {
    return (
      this.playInput === "midi" &&
      this.midiListening &&
      this.midiDevicesCache.length > 0
    );
  }

  private applyMidiPreference(devices: MidiDeviceInfo[], fromHotplug: boolean): void {
    const hasMidi = devices.some((d) => d.state === "connected");
    if (fromHotplug && this.autoPreferMidi) {
      if (hasMidi) {
        this.playInput = "midi";
        this.midiListening = true;
        this.qwerty.setEnabled(false);
      } else {
        this.playInput = "laptop";
        this.qwerty.setEnabled(true);
      }
    } else if (!hasMidi) {
      // Always fall back to laptop when cable/BT goes away.
      this.playInput = "laptop";
      this.qwerty.setEnabled(true);
    } else if (this.playInput === "midi") {
      this.qwerty.setEnabled(false);
    }
  }

  private onMidiDevicesChanged(devices: MidiDeviceInfo[]): void {
    const prevCount = this.midiDevicesCache.length;
    this.midiDevicesCache = devices;
    const nextCount = devices.filter((d) => d.state === "connected").length;
    // Re-enable auto prefer when a device newly appears after none.
    if (prevCount === 0 && nextCount > 0) {
      this.autoPreferMidi = true;
    }
    this.applyMidiPreference(devices, true);
    this.emitStatusNow();
  }

  private handleMidi(e: MidiNoteEvent): void {
    if (!this.isMidiActive()) return;
    // Same hot path as QWERTY: audio first, never await on the MIDI callback.
    this.synth.unlockSync();
    const isOn = e.type === "noteon" && e.velocity > 0;
    let sync: SyncGradeResult | undefined;
    if (isOn) {
      this.synth.noteOn(e.midiNote, e.velocity);
      this.activeNotes.add(e.midiNote);
      sync = this.gradePracticeOrExam(e.midiNote, e.velocity);
    } else {
      this.synth.noteOff(e.midiNote, e.velocity);
      this.activeNotes.delete(e.midiNote);
    }
    const midiNote = e.midiNote;
    const velocity = e.velocity;
    const timestamp = e.timestamp;
    queueMicrotask(() => {
      if (
        isOn &&
        sync == null &&
        this.syncGrader.isEnabled() &&
        this.metronome.isRunning
      ) {
        sync = this.syncGrader.grade(this.metronome, this.synth.currentTime);
      }
      const event: TutorNoteEvent = {
        type: isOn ? "noteon" : "noteoff",
        midiNote,
        velocity,
        source: "midi",
        timestamp,
        sync,
      };
      this.performanceRecorder.note(event.type, midiNote, velocity, "midi");
      for (const h of this.noteHandlers) h(event);
      this.emitStatusSoon();
    });
  }

  private handleQwerty(e: QwertyNoteEvent): void {
    if (!this.isQwertyActive()) return;
    // Audio already fired via setAudioSink — this handler is UI/grading only.
    const isOn = e.type === "noteon";
    const midiNote = e.midiNote;
    const velocity = e.velocity;
    const timestamp = performance.now();
    queueMicrotask(() => {
      let sync = this.pendingSyncGrade;
      this.pendingSyncGrade = undefined;
      if (
        isOn &&
        sync == null &&
        this.syncGrader.isEnabled() &&
        this.metronome.isRunning
      ) {
        sync = this.syncGrader.grade(this.metronome, this.synth.currentTime);
      }
      const event: TutorNoteEvent = {
        type: isOn ? "noteon" : "noteoff",
        midiNote,
        velocity,
        source: "qwerty",
        timestamp,
        sync,
      };
      this.performanceRecorder.note(event.type, midiNote, velocity, "qwerty");
      for (const h of this.noteHandlers) h(event);
      this.emitStatusSoon();
    });
  }

  private routeNote(
    type: "noteon" | "noteoff",
    midiNote: number,
    velocity: number,
    source: InputSource,
    timestamp = performance.now(),
  ): void {
    const isOn = type === "noteon" && velocity > 0;

    // Audio first — keep pointer/MIDI hot path free of grading + UI work.
    if (isOn) {
      this.synth.noteOn(midiNote, velocity);
      this.activeNotes.add(midiNote);
    } else {
      this.synth.noteOff(midiNote, velocity);
      this.activeNotes.delete(midiNote);
    }

    queueMicrotask(() => {
      let sync: SyncGradeResult | undefined;
      if (isOn) sync = this.gradePracticeOrExam(midiNote, velocity);
      if (
        isOn &&
        sync == null &&
        this.syncGrader.isEnabled() &&
        this.metronome.isRunning
      ) {
        sync = this.syncGrader.grade(this.metronome, this.synth.currentTime);
      }
      const event: TutorNoteEvent = {
        type: isOn ? "noteon" : "noteoff",
        midiNote,
        velocity,
        source,
        timestamp,
        sync,
      };
      this.performanceRecorder.note(event.type, midiNote, velocity, source);
      for (const h of this.noteHandlers) h(event);
      this.emitStatusSoon();
    });
  }

  private emitStatusNow(): void {
    if (this.statusRaf != null) {
      cancelAnimationFrame(this.statusRaf);
      this.statusRaf = null;
    }
    const status = this.getStatus();
    for (const h of this.statusHandlers) h(status);
  }

  private emitStatusSoon(): void {
    if (this.statusRaf != null) return;
    this.statusRaf = requestAnimationFrame(() => {
      this.statusRaf = null;
      const status = this.getStatus();
      for (const h of this.statusHandlers) h(status);
    });
  }
}

let singleton: PianoTutorCore | null = null;

export function getPianoTutorCore(): PianoTutorCore {
  if (!singleton) singleton = new PianoTutorCore();
  return singleton;
}
